import { API, graphqlOperation } from "aws-amplify";
import { listTenants } from "../graphql/queries";

/**
 * Helper to fetch all data from a paginated GraphQL query.
 * Handles nextToken pagination automatically.
 * @param {string} query - GraphQL query string
 * @param {string} queryName - Name of the query (e.g., "listSites", "listTenants")
 * @param {object} args - Query arguments (filter, limit, etc.)
 * @return {array} All items from all pages
 */
async function fetchAllPages(query, queryName, args) {
  let allItems = [];
  let nextToken = null;

  do {
    if (nextToken) {
      args.nextToken = nextToken;
    }

    const response = await API.graphql(graphqlOperation(query, args));
    const data = response.data[queryName];

    allItems = allItems.concat(data.items || []);
    nextToken = data.nextToken;
  } while (nextToken);

  return allItems;
}

// this is based on listTenants, but also asks for details of the site
export const listTenantsWithSites = `query ListTenants(
  $filter: ModelTenantFilterInput
  $limit: Int
  $nextToken: String
) {
  listTenants(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      company
      disabled
      sites {
        items { id siteName rmsName mapName disabled }
      }
    }
    nextToken
  }
}
`;

// Admin-only query with timezone and other admin fields
export const listTenantsWithSitesAdmin = `query ListTenants(
  $filter: ModelTenantFilterInput
  $limit: Int
  $nextToken: String
) {
  listTenants(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      company
      disabled
      sites {
        items { id siteName rmsName mapName timezone disabled }
      }
    }
    nextToken
  }
}
`;

// This is based on the auto-generated query listSites, but without asking for
// rmsUser and rmsPw, because a tenant can't read those so this avoids an
// unauthorized exception. The second query below does include that information.
const listTenantSites = `query ListSites(
  $filter: ModelSiteFilterInput
  $limit: Int
  $nextToken: String
  ){
      listSites(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
          id
          tid
          siteName
          emailsToNotify
          alarmThresholds
          restrict_ui_page
          disabled
          securityRound_action
          securityRound_wait_time
          include_tags_in_round
          allow_image_store
          allow_securityRound_tpapp
          door_detect_locations
          display_detect_locations
          enablemonitoringdata
      }
      nextToken
      }
  }
  `;

const listSysAdminTenantSites = `query ListSites(
  $filter: ModelSiteFilterInput
  $limit: Int
  $nextToken: String
  ){
    listSites(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        tid
        siteName
        rmsName
        rmsUser
        mapName
        emailsToNotify
        adminEmailsToNotify
        alarmThresholds
        timezone
        recharge_threshold_min
        recharge_threshold_max
        restrict_ui_page
        disabled
        securityRound_action
        securityRound_wait_time
        include_tags_in_round
        allow_image_store
        allow_securityRound_tpapp
        door_detect_locations
        display_detect_locations
        enablemonitoringdata
      }
      nextToken
    }
  }
  `;
// Function to build a string to update only basic things for the given site.
const updateSiteOperation = (fields) =>
  `mutation updateSiteSimple{
         updateSite(input:{
             id: "${fields.siteid}"
             emailsToNotify: ${JSON.stringify(fields.emailsToNotify)}
             enablemonitoringdata: ${fields.enablemonitoringdata}
             allow_image_store: ${fields.allow_image_store}
         }){
             id
             emailsToNotify
             enablemonitoringdata
             allow_image_store
         }
     }`;

// Same as above, but for sysadmin users, extended for other fields.
// Attempt to update the RMS password only if we have an actual
// value to update.
// For the response, do not ask for adminEmailsToNotify or rmsUser because
// an Amplify/Appsync bug (apparently) returns null instead of real values.
// And rmsPw is never readable, by design.
const adminUpdateSiteOperation = (fields) => {
  let rmsPwField = "";
  if (fields.rmsPw) {
    rmsPwField = `rmsPw: "${fields.rmsPw}"`;
  }
  return (
    `mutation adminUpdateSiteSimple{
     updateSite(input:{
         id: "${fields.siteid}"
         emailsToNotify: ${JSON.stringify(fields.emailsToNotify)}
         adminEmailsToNotify: ${JSON.stringify(fields.adminEmailsToNotify)}
         rmsName: "${fields.rmsName}"
         rmsUser: "${fields.rmsUser}"
         ${rmsPwField}
         mapName: "${fields.mapName}"
         siteName: "${fields.siteName}"
         timezone: "${fields.timezone}"
         recharge_threshold_min: ` +
    (fields.recharge_threshold_min ? `${fields.recharge_threshold_min}` : `50`) +
    `
         recharge_threshold_max: ` +
    (fields.recharge_threshold_max ? `${fields.recharge_threshold_max}` : `80`) +
    `
         restrict_ui_page: ${JSON.stringify(JSON.stringify(fields.restrict_ui_page))}
         enablemonitoringdata: ${fields.enablemonitoringdata}
         allow_image_store: ${fields.allow_image_store}
      }){
         id
         emailsToNotify
         rmsName
         mapName
         siteName
         timezone
         recharge_threshold_min
         recharge_threshold_max
         restrict_ui_page
         enablemonitoringdata
         allow_image_store
     }
 }`
  );
};

const updateLocationBehaviorOperation = (
  siteid,
  securityRound_action,
  securityRound_wait_time
) =>
  `mutation updateLocationBehavior{
      updateSite(input:{
          id: "${siteid}"
          securityRound_action: "${securityRound_action}"
          securityRound_wait_time: ${securityRound_wait_time}
      }){
          id
          securityRound_action
          securityRound_wait_time
      }
    }`;
const updateRoundSettingsOperation = (
  siteid,
  include_tags_in_round,
  allow_securityRound_tpapp,
  thresholds,
  door_detect_locations,
  display_detect_locations
) => `
  mutation updateRoundSettings {
    updateSite(input: {
      id: "${siteid}"
      include_tags_in_round: ${include_tags_in_round}
      allow_securityRound_tpapp: ${allow_securityRound_tpapp}
      alarmThresholds: ${thresholds}
      door_detect_locations: ${door_detect_locations}
      display_detect_locations: ${display_detect_locations}
    }) {
      id
      include_tags_in_round
      allow_securityRound_tpapp
      alarmThresholds
      door_detect_locations
      display_detect_locations
    }
  }
`;
/**
 * Get a list of sorted tenants, with a list of sites for each tenant.
 * @param {boolean} includeDisabled false means do not include disabled tenants and sites
 * @return {array} list of tenants
 */
async function getTenants(includeDisabled) {
  // It seems the limit applies to all data read from DynamoDB, counting the items
  // filtered out before sending the response.
  // the limit affects how many tenants we can read when logged in as sysadmin.
  let args = { limit: 100 };
  // let the backend query filter out disabled tenants
  if (!includeDisabled) {
    args["filter"] = { disabled: { ne: true } }; // anything not true is interpreted as the tenant is enabled
  }
  let tenants = await fetchAllPages(listTenants, "listTenants", args);

  // sort Tenants based on company name
  tenants.sort((a, b) => (a.company.toLowerCase() > b.company.toLowerCase() ? 1 : -1));
  return tenants;
}

/**
 * Get a list of sorted tenants, with a list of sites for each tenant.
 * @param {boolean} includeDisabled false means do not include disabled tenants and sites
 * @return {array} example structure, showing just one tenant with one site:
 * [
 *   {
 *       "id": "71b0a0c4-245e-4ee2-af56-123e128861a3",
 *       "company": "Ava Robotics (dev)",
 *       "disabled": null,  // default is enabled
 *       "sites": {
 *           "items": [
 *               {
 *                   "id": "befa5bfb-b5e0-4c63-9bc7-4c9db4ad2e3d",
 *                   "siteName": "markd_test",
 *                   "rmsName": "XXmarkd1.ava8.net",
 *                   "mapName": "Ramp_Testing",
 *                   "disabled": null
 *               },
 *           ]
 *       }
 *   }
 * ]
 */
async function getTenantsWithSites(includeDisabled) {
  // From experimentation, it seems the limit applies to number of tenants, but the number
  // of sites returned per tenant can be larger than the limit.
  let args = { limit: 100 };
  // let the backend query filter out disabled tenants
  if (!includeDisabled) {
    args["filter"] = { disabled: { ne: true } }; // anything not true is interpreted as the tenant is enabled
  }
  let tenants = await fetchAllPages(listTenantsWithSites, "listTenants", args);

  // remove disabled sites
  if (!includeDisabled) {
    tenants.forEach(
      (tenant) => (tenant.sites.items = tenant.sites.items.filter((site) => !site.disabled))
    );
  }

  // sort Tenants based on company name
  tenants.sort((a, b) => (a.company.toLowerCase() > b.company.toLowerCase() ? 1 : -1));
  return tenants;
}

/**
 * Get a list of sorted sites
 * @param {string} tenantid if falsy, find all sites for all tenants
 * @param {boolean} getAdminFields true to ask for extra fields available only to admins
 * @param {boolean} includeDisabled false means do not include disabled sites
 * @return {array} list of sites
 */
async function getSites(tenantid, getAdminFields, includeDisabled) {
  let sites = {};
  const args = { limit: 100 };
  const query = getAdminFields ? listSysAdminTenantSites : listTenantSites;
  // There could be no filter, or filter for tenant or enabled or both
  if (tenantid) {
    args.filter = { tid: { eq: tenantid } };
  }
  if (!includeDisabled) {
    if (!args.filter) {
      args.filter = { disabled: { ne: true } };
    } else {
      args.filter.disabled = { ne: true };
    }
  }
  sites = await fetchAllPages(query, "listSites", args);
  //sort sites based on siteName
  sites.sort((a, b) => (a.siteName.toLowerCase() > b.siteName.toLowerCase() ? 1 : -1));
  return sites;
}

/**
 * Update Site object fields in the database.
 */
async function updateSite(sysadmin, fields) {
  //siteid, emailsToNotify, adminEmailsToNotify) {
  let response = "";
  if (sysadmin) {
    let site_op = adminUpdateSiteOperation(fields);
    let gql_op = graphqlOperation(site_op, {});
    response = await API.graphql(gql_op);
  } else {
    response = await API.graphql(graphqlOperation(updateSiteOperation(fields), {}));
  }

  return response;
}

/**
 * Update securityRound_action object and securityRound_wait_time object fields in the database.
 */
async function updateLocationBehavior(siteid, securityRound_action, securityRound_wait_time) {
  let response = null;
  try {
    console.log(
      "site ",
      siteid,
      " securityRound_action: ",
      securityRound_action,
      " securityRound_wait_time: ",
      securityRound_wait_time
    );
    response = await API.graphql(
      graphqlOperation(
        updateLocationBehaviorOperation(siteid, securityRound_action, securityRound_wait_time),
        {}
      )
    );
  } catch (e) {
    console.error("updateLocationBehavior", e);
  }
  return response;
}

/**
 * Update include_tags_in_round, allow_securityRound_tpapp, alarmThresholds
 * in the Site database.
 */
async function updateRoundSettings(
  siteid,
  include_tags_in_round,
  allow_securityRound_tpapp,
  thresholds,
  door_detect_locations,
  display_detect_locations
) {
  let response = null;
  try {
    console.log(
      "site ",
      siteid,
      " include_tags_in_round: ",
      include_tags_in_round,
      " allow_securityRound_tpapp: ",
      allow_securityRound_tpapp,
      " thresholds: ",
      thresholds,
      " door_detect_locations: ",
      door_detect_locations,
      " display_detect_locations: ",
      display_detect_locations
    );
    const includeTagJSON = JSON.stringify(JSON.stringify(include_tags_in_round));
    const thresholdsJSON = JSON.stringify(JSON.stringify(thresholds));
    const doorDetectJSON = JSON.stringify(door_detect_locations || []);
    const displayDetectJSON = JSON.stringify(display_detect_locations || []);

    // Call the GraphQL mutation
    response = await API.graphql(
      graphqlOperation(
        updateRoundSettingsOperation(
          siteid,
          includeTagJSON,
          allow_securityRound_tpapp,
          thresholdsJSON,
          doorDetectJSON,
          displayDetectJSON
        ),
        {}
      )
    );
  } catch (e) {
    console.error("updateRoundSettings", e);
  }
  return response;
}

/**
 * Get a list of sorted tenants with sites - ADMIN VERSION with timezone information
 * This function should only be called by admin pages/components
 * @param {boolean} includeDisabled false means do not include disabled tenants and sites
 * @return {array} same structure as getTenantsWithSites but includes timezone field
 */
async function getTenantsWithSitesAdmin(includeDisabled) {
  let args = { limit: 100 };
  // let the backend query filter out disabled tenants
  if (!includeDisabled) {
    args["filter"] = { disabled: { ne: true } };
  }

  let tenants = await API.graphql(graphqlOperation(listTenantsWithSitesAdmin, args));
  let nextToken = tenants.data.listTenants.nextToken;
  tenants = tenants.data.listTenants.items;
  while (nextToken !== null) {
    let newTenant = {};
    args["nextToken"] = nextToken;
    newTenant = await API.graphql(graphqlOperation(listTenantsWithSitesAdmin, args));
    nextToken = newTenant.data.listTenants.nextToken;
    tenants = tenants.concat(newTenant.data.listTenants.items);
  }

  // remove disabled sites
  if (!includeDisabled) {
    tenants.forEach(
      (tenant) => (tenant.sites.items = tenant.sites.items.filter((site) => !site.disabled))
    );
  }

  // sort Tenants based on company name
  tenants.sort((a, b) => (a.company.toLowerCase() > b.company.toLowerCase() ? 1 : -1));
  return tenants;
}

export {
  fetchAllPages,
  getTenants,
  getTenantsWithSites,
  getTenantsWithSitesAdmin,
  getSites,
  updateSite,
  updateLocationBehavior,
  updateRoundSettings,
};
